// Owner-facing modal for reassigning the guard at a specific slot on a job.
// Used from LiveJobs (in-progress jobs) and JobInbox (scheduled). Calls
// reassignGuard(jobId, guardNumber, newGuard) on save.

import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { reassignGuard, watchGuards } from "../../services/firestore";
import { useEscapeKey } from "../../hooks/useEscapeKey";

export default function ReassignGuardModal({ job, guardNumber, onClose, onDone }) {
  useEscapeKey(() => onClose?.());
  const { userProfile } = useAuth();
  const [guards, setGuards] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState("");

  const current = (job.guardAssignments || []).find((g) => g.guardNumber === guardNumber);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    const unsub = watchGuards(userProfile.companyId, (list) => setGuards(list));
    return unsub;
  }, [userProfile?.companyId]);

  // Already-assigned guard IDs on this job (other slots) — exclude from picker
  // so we don't accidentally double-assign one person.
  const otherAssigned = new Set(
    (job.guardAssignments || [])
      .filter((g) => g.guardNumber !== guardNumber && g.guardId)
      .map((g) => g.guardId)
  );
  const available = guards.filter((g) => !otherAssigned.has(g.id));

  async function handleSave() {
    if (!selectedId && current?.guardId) {
      // No-op safeguard — user clicked Save without picking anything.
      return;
    }
    setSaving(true);
    try {
      const picked = guards.find((g) => g.id === selectedId);
      await reassignGuard(
        job.id,
        guardNumber,
        picked ? { id: picked.id, name: picked.name, defaultPayRate: picked.defaultPayRate } : null,
        { id: userProfile?.id, name: userProfile?.name }
      );
      onDone?.({ guardNumber, newGuardId: picked?.id || null, reason });
      onClose();
    } catch (err) {
      console.error("Reassign failed", err);
      alert("Reassign failed: " + (err?.message || err));
    }
    setSaving(false);
  }

  async function handleUnassign() {
    if (!current) return onClose();
    if (!window.confirm(`Unassign ${current.guardName || `Guard ${guardNumber}`} from this job?`)) return;
    setSaving(true);
    try {
      await reassignGuard(job.id, guardNumber, null, { id: userProfile?.id, name: userProfile?.name });
      onDone?.({ guardNumber, newGuardId: null, reason });
      onClose();
    } catch (err) {
      alert("Unassign failed: " + (err?.message || err));
    }
    setSaving(false);
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 250,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, width: "100%", maxWidth: 420,
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column",
      }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #eee" }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Reassign Guard {guardNumber}</div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
            {job.jobNumber} · {job.brokerName || ""}
          </div>
        </div>

        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: "#f8fafc", border: "1px solid #e6e9ef", borderRadius: 8, padding: "10px 12px", fontSize: 12 }}>
            <div style={{ color: "#888", fontSize: 10, fontWeight: 700, textTransform: "uppercase", marginBottom: 2 }}>Currently assigned</div>
            <div style={{ fontWeight: 600 }}>
              {current?.guardName || (current ? `Guard ${guardNumber}` : <span style={{ color: "#bbb", fontWeight: 400 }}>(none — slot is open)</span>)}
            </div>
            {current?.payRate && <div style={{ fontSize: 11, color: "#666" }}>${current.payRate}/hr</div>}
          </div>

          <label style={{ fontSize: 11, fontWeight: 700, color: "#555" }}>
            Replace with
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", marginTop: 4, borderRadius: 7, border: "1px solid #ddd", fontSize: 13 }}
            >
              <option value="">— pick a guard —</option>
              {available.map((g) => (
                <option key={g.id} value={g.id} disabled={g.id === current?.guardId}>
                  {g.name}{g.id === current?.guardId ? " (currently assigned)" : ""}
                </option>
              ))}
            </select>
          </label>

          <label style={{ fontSize: 11, fontWeight: 700, color: "#555" }}>
            Reason (optional)
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. flat tire, sick, schedule conflict"
              style={{ width: "100%", minHeight: 50, padding: "8px 10px", marginTop: 4, borderRadius: 7, border: "1px solid #ddd", fontSize: 12 }}
            />
          </label>

          {job.status === "inProgress" && (
            <div style={{ background: "#fff7e0", border: "1px solid #f0c878", borderRadius: 7, padding: "8px 10px", fontSize: 11, color: "#7a5400" }}>
              ⚠ This job is in progress. The replacement guard will pick up from the current step. Make sure they're briefed on what's already been completed.
            </div>
          )}
        </div>

        <div style={{ padding: "12px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", gap: 8 }}>
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <div style={{ display: "flex", gap: 8 }}>
            {current?.guardId && (
              <button className="btn" onClick={handleUnassign} disabled={saving} title="Remove this guard and leave the slot open">
                Unassign
              </button>
            )}
            <button
              className="btn btn-blue"
              onClick={handleSave}
              disabled={saving || !selectedId || selectedId === current?.guardId}
            >
              {saving ? "Saving..." : "✓ Reassign"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
